import { useState, useEffect } from 'react'
import { Container, Table, Card, Button, Alert, Badge, Spinner, Modal, Pagination } from 'react-bootstrap'
import adminService from '../../services/adminService'

function AdminHiddenContent() {
  const [hiddenScorables, setHiddenScorables] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)

  // Unhide confirmation
  const [showUnhideModal, setShowUnhideModal] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [unhiding, setUnhiding] = useState(false)

  useEffect(() => {
    loadHiddenScorables()
  }, [currentPage])

  const loadHiddenScorables = async () => {
    setLoading(true)
    try {
      const data = await adminService.getHiddenScorables({
        page: currentPage,
        per_page: 20
      })
      setHiddenScorables(data.items || data)
      setTotalPages(data.total_pages || 1)
      setTotalItems(data.total || data.length || 0)
    } catch (err) {
      setError('Failed to load hidden content')
      console.error('Failed to load hidden scorables:', err)
    } finally {
      setLoading(false)
    }
  }

  const openUnhideModal = (item) => {
    setSelectedItem(item)
    setShowUnhideModal(true)
  }

  const handleUnhide = async () => {
    if (!selectedItem) return

    setUnhiding(true)
    setError(null)

    try {
      await adminService.unhideScorable(selectedItem.scorable_id)
      setSuccess('Content unhidden successfully!')
      setShowUnhideModal(false)
      setSelectedItem(null)
      loadHiddenScorables()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to unhide content')
    } finally {
      setUnhiding(false)
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <Container className="mt-4">
      <h2>Hidden Content</h2>
      <p className="text-muted mb-4">Manage globally hidden content that is excluded from all search results.</p>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

      {loading ? (
        <div className="text-center py-5">
          <Spinner />
          <p className="text-muted mt-2">Loading hidden content...</p>
        </div>
      ) : hiddenScorables.length === 0 ? (
        <Card className="text-center py-5">
          <Card.Body>
            <p className="text-muted mb-0">No globally hidden content.</p>
          </Card.Body>
        </Card>
      ) : (
        <>
          <Card>
            <Table responsive hover className="mb-0">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Content</th>
                  <th>URL</th>
                  <th>Reason</th>
                  <th>Hidden Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {hiddenScorables.map(item => (
                  <tr key={item.id}>
                    <td>{item.scorable_id}</td>
                    <td>
                      <div style={{ maxWidth: '200px' }}>
                        <span className="text-truncate d-block">
                          {item.scorable?.pretty_name || 'Unnamed'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div style={{ maxWidth: '250px' }}>
                        <a
                          href={item.scorable?.thing}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-truncate d-block small"
                        >
                          {item.scorable?.thing || 'N/A'}
                        </a>
                      </div>
                    </td>
                    <td>
                      <Badge bg={item.reason === 'admin_action' ? 'primary' : 'secondary'}>
                        {item.reason === 'admin_action' ? 'Admin Action' : item.reason || 'Unknown'}
                      </Badge>
                    </td>
                    <td><small>{formatDate(item.created)}</small></td>
                    <td>
                      <Button
                        variant="outline-warning"
                        size="sm"
                        onClick={() => openUnhideModal(item)}
                      >
                        Unhide
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <small className="text-muted">
                Showing page {currentPage} of {totalPages} ({totalItems} total hidden items)
              </small>
              <Pagination className="mb-0">
                <Pagination.First
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(1)}
                />
                <Pagination.Prev
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                />
                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  const page = Math.max(1, currentPage - 2) + i
                  if (page > totalPages) return null
                  return (
                    <Pagination.Item
                      key={page}
                      active={page === currentPage}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </Pagination.Item>
                  )
                })}
                <Pagination.Next
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                />
                <Pagination.Last
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(totalPages)}
                />
              </Pagination>
            </div>
          )}
        </>
      )}

      {/* Unhide Confirmation Modal */}
      <Modal show={showUnhideModal} onHide={() => setShowUnhideModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Unhide Content</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedItem && (
            <>
              <p>Are you sure you want to unhide this content?</p>
              <Card>
                <Card.Body>
                  <p className="mb-1">
                    <strong>{selectedItem.scorable?.pretty_name || 'Unnamed'}</strong>
                  </p>
                  <p className="mb-0 small text-muted">
                    {selectedItem.scorable?.thing || 'N/A'}
                  </p>
                </Card.Body>
              </Card>
              <Alert variant="warning" className="mt-3 mb-0">
                This content will become visible in search results again for all users.
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUnhideModal(false)}>
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={handleUnhide}
            disabled={unhiding}
          >
            {unhiding ? 'Unhiding...' : 'Unhide Content'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}

export default AdminHiddenContent
